/**
 * Backend Cache Service
 * Integrates frontend cache with backend cache management APIs
 */

import api from './api';
import { cacheManager } from './cacheManager';

class BackendCacheService {
  constructor() {
    this.baseURL = '/api/cache';
    this.retryCount = 2; // Reduced from 3 to prevent rate limiting
    this.retryDelay = 2000; // Increased to 2 seconds
    this.lastRequestTime = 0;
    this.minRequestInterval = 1000; // Minimum 1 second between requests
  }

  /**
   * Rate limiting helper to prevent 429 errors
   * @private
   */
  async _rateLimitRequest() {
    const now = Date.now();
    const timeSinceLastRequest = now - this.lastRequestTime;
    
    if (timeSinceLastRequest < this.minRequestInterval) {
      const waitTime = this.minRequestInterval - timeSinceLastRequest;
      console.log(`⏳ Rate limiting: waiting ${waitTime}ms before backend request`);
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
    
    this.lastRequestTime = Date.now();
  }

  /**
   * Force refresh cache for a specific folder on both frontend and backend
   * @param {string} folderPath - The folder path to refresh
   * @param {boolean} clearFrontendFirst - Whether to clear frontend cache first
   */
  async forceRefreshFolder(folderPath, clearFrontendFirst = true) {
    try {
      console.log('🔄 Backend Cache Service: Force refreshing folder:', folderPath);

      // Clear frontend cache first if requested
      if (clearFrontendFirst) {
        cacheManager.invalidateHierarchy(folderPath);
        console.log('🧹 Cleared frontend cache for:', folderPath);
      }

      // Rate limit the request
      await this._rateLimitRequest();

      // Call backend to refresh cache
      const response = await api.post(`${this.baseURL}/refresh`, {
        folderPath: folderPath
      });

      if (response.data.success) {
        console.log('✅ Backend cache refreshed successfully for:', folderPath);
        return {
          success: true,
          message: response.data.message,
          folderPath: folderPath
        };
      } else {
        console.warn('⚠️ Backend cache refresh failed:', response.data.error);
        return {
          success: false,
          error: response.data.error,
          folderPath: folderPath
        };
      }
    } catch (error) {
      console.error('❌ Backend cache refresh error:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message,
        folderPath: folderPath
      };
    }
  }

  /**
   * Clear all caches on both frontend and backend
   */
  async clearAllCaches() {
    try {
      console.log('🧹 Backend Cache Service: Clearing all caches...');

      // Clear frontend cache first
      cacheManager.clear();
      console.log('✅ Frontend cache cleared');

      // Clear backend cache
      const response = await api.post(`${this.baseURL}/clear-all`);

      if (response.data.success) {
        console.log('✅ Backend caches cleared successfully');
        return {
          success: true,
          message: response.data.message,
          clearedCount: response.data.clearedCount
        };
      } else {
        console.warn('⚠️ Backend cache clear failed');
        return {
          success: false,
          error: 'Backend cache clear failed'
        };
      }
    } catch (error) {
      console.error('❌ Backend cache clear error:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  }

  /**
   * Invalidate cache for specific folder on both frontend and backend
   * @param {string} folderPath - The folder path to invalidate
   * @param {boolean} includeParent - Whether to include parent folders
   * @param {boolean} includeChildren - Whether to include child folders
   */
  async invalidateFolder(folderPath, includeParent = true, includeChildren = false) {
    try {
      console.log('🧹 Backend Cache Service: Invalidating folder:', folderPath);

      // Invalidate frontend cache first
      const frontendInvalidated = cacheManager.invalidateHierarchy(folderPath);
      console.log(`🗑️ Frontend: Invalidated ${frontendInvalidated} cache entries`);

      // Invalidate backend cache
      const response = await api.post(`${this.baseURL}/invalidate`, {
        folderPath,
        includeParent,
        includeChildren
      });

      if (response.data.success) {
        console.log('✅ Backend cache invalidated successfully');
        return {
          success: true,
          frontendInvalidated,
          backendInvalidated: response.data.invalidated,
          errors: response.data.errors || []
        };
      } else {
        console.warn('⚠️ Backend cache invalidation failed');
        return {
          success: false,
          frontendInvalidated,
          backendInvalidated: [],
          errors: ['Backend invalidation failed']
        };
      }
    } catch (error) {
      console.error('❌ Backend cache invalidation error:', error);
      return {
        success: false,
        frontendInvalidated: 0,
        backendInvalidated: [],
        errors: [error.response?.data?.error || error.message]
      };
    }
  }

  /**
   * Get cache status for a folder from backend
   * @param {string} folderPath - The folder path to check
   */
  async getCacheStatus(folderPath) {
    try {
      const response = await api.get(`${this.baseURL}/status`, {
        params: { folderPath }
      });

      if (response.data.success) {
        return {
          success: true,
          status: response.data.status
        };
      } else {
        return {
          success: false,
          error: 'Failed to get cache status'
        };
      }
    } catch (error) {
      console.error('❌ Cache status error:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  }

  /**
   * Batch invalidate multiple folders
   * @param {string[]} folderPaths - Array of folder paths to invalidate
   * @param {string} reason - Reason for batch invalidation
   */
  async batchInvalidate(folderPaths, reason = 'frontend-batch') {
    try {
      console.log(`🧹 Backend Cache Service: Batch invalidating ${folderPaths.length} folders`);

      // Invalidate frontend caches first
      let frontendTotal = 0;
      folderPaths.forEach(path => {
        frontendTotal += cacheManager.invalidateHierarchy(path);
      });

      // Batch invalidate backend caches
      const response = await api.post(`${this.baseURL}/batch-invalidate`, {
        folderPaths,
        reason
      });

      if (response.data.success) {
        console.log('✅ Batch cache invalidation completed');
        return {
          success: true,
          frontendInvalidated: frontendTotal,
          backendResult: response.data
        };
      } else {
        return {
          success: false,
          frontendInvalidated: frontendTotal,
          error: 'Backend batch invalidation failed'
        };
      }
    } catch (error) {
      console.error('❌ Batch cache invalidation error:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  }

  /**
   * Smart cache invalidation for file operations
   * @param {string} operation - The operation type ('delete', 'upload', 'create', 'move')
   * @param {string} filePath - The file path that was affected
   * @param {string} newPath - New path for move operations
   */
  async invalidateAfterOperation(operation, filePath, newPath = null) {
    try {
      console.log(`🔄 Backend Cache Service: Invalidating after ${operation} operation`);

      const parentPath = filePath.substring(0, filePath.lastIndexOf('/')) || '/';
      const pathsToInvalidate = [parentPath];

      // Add additional paths based on operation
      switch (operation) {
        case 'delete':
          // For delete, also invalidate the deleted item path if it's a folder
          pathsToInvalidate.push(filePath);
          break;
        case 'move':
          // For move, invalidate both old and new parent paths
          if (newPath) {
            const newParentPath = newPath.substring(0, newPath.lastIndexOf('/')) || '/';
            if (newParentPath !== parentPath) {
              pathsToInvalidate.push(newParentPath);
            }
          }
          break;
        case 'upload':
        case 'create':
          // For uploads and creates, just parent path is sufficient
          break;
        default:
          // No additional invalidation needed for unknown operations
          break;
      }

      // Remove duplicates
      const uniquePaths = [...new Set(pathsToInvalidate)];

      return await this.batchInvalidate(uniquePaths, `${operation}-operation`);

    } catch (error) {
      console.error('❌ Post-operation cache invalidation error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Health check - verify backend cache service is available
   */
  async healthCheck() {
    try {
      // Try to get cache status for root directory as a health check
      const result = await this.getCacheStatus('/');
      return {
        healthy: result.success,
        error: result.success ? null : result.error
      };
    } catch (error) {
      return {
        healthy: false,
        error: error.message
      };
    }
  }

  /**
   * Retry wrapper for cache operations
   * @private
   */
  async _retryOperation(operation, ...args) {
    let lastError;
    
    for (let attempt = 1; attempt <= this.retryCount; attempt++) {
      try {
        return await operation(...args);
      } catch (error) {
        lastError = error;
        console.log(`⚠️ Cache operation attempt ${attempt} failed:`, error.message);
        
        if (attempt < this.retryCount) {
          await new Promise(resolve => setTimeout(resolve, this.retryDelay * attempt));
        }
      }
    }
    
    throw lastError;
  }
}

// Export singleton instance
export const backendCacheService = new BackendCacheService();

// Export utility functions
export const forceRefreshFolder = (folderPath) => 
  backendCacheService.forceRefreshFolder(folderPath);

export const clearAllCaches = () => 
  backendCacheService.clearAllCaches();

export const invalidateAfterOperation = (operation, filePath, newPath) => 
  backendCacheService.invalidateAfterOperation(operation, filePath, newPath);

export const getCacheStatus = (folderPath) => 
  backendCacheService.getCacheStatus(folderPath);

export default backendCacheService;
